import { useEffect, useRef, useState, useCallback, type ReactNode } from 'react'
import { useConversation, type ClientTools, type SessionConfig } from '@elevenlabs/react'
import { Button } from '@/components/ui/button'

import { Input } from '@/components/ui/input'
import {
    Mic,
    MicOff,
    Phone,
    PhoneOff,
    Send,
    Loader2,
    Volume2,
    FileUp,
    MessageSquare,
    NotebookPen,
    Languages,
    X,
    Copy,
    ArrowUp,
    ThumbsUp,
    ThumbsDown
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { logger } from '@/lib/utils/logger'
import { supabase } from '@/lib/supabase'
import { config } from '@/lib/config'
import { requestElevenLabsAuth, safeVoiceError } from '@/lib/elevenlabs-auth'
import { getVoiceTransport } from '@/lib/voice-transport'
import type { ConnectionMode } from '@/lib/voice-console/state/types'
import { useAgentPWA } from '@/hooks/useAgentPWA'
import { useWakeLock } from '@/hooks/useWakeLock'
import { InstallAgentButton } from './InstallAgentButton'
import { VoiceAgentFace } from './VoiceAgentFace'

/**
 * Imperative actions exposed to a parent component via onActionsReady.
 *
 * sendContextualUpdate – injects context into the agent WITHOUT triggering a
 *   spoken response. Useful for silently passing page-navigation events,
 *   background data, or any state change the agent should know about.
 *
 * sendUserActivity – signals that the user is actively interacting (typing,
 *   scrolling, etc.), which prevents the agent from interrupting or timing out.
 */
export type ConversationActions = {
    sendContextualUpdate: (text: string) => void
    sendUserActivity: () => void
}

interface VoiceAgentSDKProps {
    agentId: string
    initialAutoConnect?: boolean
    agentName?: string
    onLeave?: () => void
    forceMobileMode?: boolean
    userId: string
    authUserId?: string // Supabase auth.users table ID
    botId: string
    role?: string
    fullName?: string
    email?: string
    provider?: string
    issuer?: string
    providerId?: string
    phone?: string
    /** Callback to fetch a fresh Signed Action Token on each connect */
    onRefreshSAT?: () => Promise<string | null>
    /**
     * Client-side tools the agent can invoke during the conversation.
     * Keys are tool names configured in the ElevenLabs agent; values are the
     * functions executed on the client when the agent calls them.
     *
     * @example
     * clientTools={{
     *   openCalendar: ({ date }) => { navigate(`/calendar?date=${date}`) },
     *   showAlert:    ({ message }) => { toast(message) },
     * }}
     */
    clientTools?: ClientTools
    /**
     * Called each time the conversation connects, providing access to
     * sendContextualUpdate and sendUserActivity so the parent can inject
     * context or signal user activity from outside the component.
     */
    onActionsReady?: (actions: ConversationActions) => void
    /**
     * Optional opt-in: when true, typing in the input sends SDK user-activity
     * pings on each keystroke. Disabled by default to avoid altering the
     * agent's turn-taking behavior unless explicitly requested.
     */
    autoSignalUserActivityOnType?: boolean
    /** Called when ElevenLabs session connects */
    onSdkConnect?: () => void
    /** Called when ElevenLabs session disconnects */
    onSdkDisconnect?: (intent: DisconnectIntent) => void
    /**
     * Called when the SDK conversation mode changes.
     * modeValue is the raw string from ElevenLabs: 'listening' | 'speaking' | 'thinking' | etc.
     */
    onSdkModeChange?: (modeValue: string) => void
    /** Called when a new transcript message arrives (user or agent) */
    onSdkMessage?: (msg: { role: 'user' | 'agent'; text: string; timestamp: number }) => void
    /** Called once on mount with imperative control functions */
    onControlsReady?: (controls: {
        connect: (mode: ConnectionMode) => void
        disconnect: () => void
        switchMode: (mode: ConnectionMode) => void
        toggleMic: () => void
        toggleMeeting: () => void
        toggleTranslator: () => void
        getInputVolume: () => number
        getOutputFrequencyData: () => Uint8Array | null
    }) => void
    onConnectionModeChange?: (mode: ConnectionMode | null) => void
    /** Called when mic muted state changes */
    onMicMutedChange?: (muted: boolean) => void
    /** Called when agent mode changes (meeting / translator / default) */
    onAgentModeChange?: (mode: 'default' | 'meeting' | 'translator') => void
    /** Injected between the spectrum and chat sections (used by CockpitShell for OperationsFloor) */
    topSlot?: ReactNode
    /**
     * Called the moment the user initiates a connection attempt — before microphone
     * permission, SAT refresh, or WebRTC negotiation. Use this to start pre-session
     * initialization work (e.g. Ably subscription) in parallel with the SDK setup.
     */
    onConnecting?: () => void
}

type Message = {
    id: string
    role: 'user' | 'agent'
    text: string
    timestamp: number
}

type AgentMode = 'default' | 'meeting' | 'translator'
type MessageFeedback = 'like' | 'dislike'
type DisconnectIntent = 'manual' | 'mode-switch' | 'unknown'

const TRANSLATOR_SYSTEM_PROMPT = `You are a Universal Simultaneous Interpreter.

Your operation has two phases:
PHASE 1: CONFIGURATION
- Your very first input will be a specification of two languages (e.g., "Russian and Spanish" or "Source: English, Target: Japanese").
- You must parse this input to establish {Language_A} and {Language_B}.
- Acknowledge this configuration silently and immediately switch to Phase 2.

PHASE 2: TRANSLATION LOOP
- For all subsequent inputs, detect the language automatically.
- If input is {Language_A} -> Translate immediately to {Language_B}.
- If input is {Language_B} -> Translate immediately to {Language_A}.
- OUTPUT RULES: Provide ONLY the translation. Do not add filler ("Here is the translation"), do not explain, and do not break character. Maintain the original tone and first-person perspective.

- SESSION CONTINUITY: A completed translation or a period of silence does not
end the interpreting session. Never invoke an end-call tool because a turn is
complete or the user pauses. Remain connected and wait for the next utterance.
Only end when the user explicitly asks to leave translator mode or end the call.

* After the initial setup treat everything you hear as part of the discussion and translate that,
do not assume use is addressing again, it should all be translated as instructed!

`
const ARRIVAL_GREETING_CONTEXT_MESSAGE = '[SYSTEM: The user just arrived. greet and follow the insructions under ACTIVE_PREFERENCES_START]'
const ARRIVAL_GREETING_SEND_DELAY_MS = 120
const TRANSLATOR_KEEP_ALIVE_MS = 25_000
const MEETING_END_ACK_DEDUPE_MS = 15_000
const MEETING_TOGGLE_LOCK_MS = 500

function isMeetingEndedAcknowledgement(text: string): boolean {
    return text
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[.!?…]+$/u, '') === 'meeting ended'
}

export function VoiceAgentSDK({
    agentId,
    initialAutoConnect = false,
    agentName = 'Voice Assistant',
    onLeave,
    forceMobileMode = false,
    userId,
    authUserId,
    botId,
    role,
    fullName,
    email,
    provider,
    issuer,
    providerId,
    phone,
    onRefreshSAT,
    clientTools,
    onActionsReady,
    autoSignalUserActivityOnType = false,
    onSdkConnect,
    onSdkDisconnect,
    onSdkModeChange,
    onSdkMessage,
    onControlsReady,
    onConnectionModeChange,
    onMicMutedChange,
    onAgentModeChange,
    topSlot,
    onConnecting,
}: VoiceAgentSDKProps) {
    const [messages, setMessages] = useState<Message[]>([])
    const [inputText, setInputText] = useState('')
    const [connectionError, setConnectionError] = useState<string | null>(null)
    const showVoiceDiagnostics = new URLSearchParams(window.location.search).get('debug') === 'true'
    const transportRef = useRef<'webrtc' | 'websocket'>('webrtc')
    const relayRef = useRef(false)
    const [audioLevels, setAudioLevels] = useState({ input: 0, output: 0 })
    const [isMicMuted, setIsMicMuted] = useState(false)
    const [agentMode, setAgentMode] = useState<AgentMode>('default')
    const agentModeRef = useRef<AgentMode>('default') // Ref to track current mode in callbacks
    // Captures the mode requested for a replacement session before any async
    // disconnect/auth/microphone work can reset the currently rendered mode.
    const pendingAgentModeOnConnectRef = useRef<AgentMode | null>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const [showMeetingTip, setShowMeetingTip] = useState(false)
    const [showTranslateTip, setShowTranslateTip] = useState(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    // Tracks the SDK-reported conversation mode (listening / speaking / thinking).
    // Updated via onModeChange and used to show more precise status labels.
    const [conversationMode, setConversationMode] = useState<string>('listening')
    // Tracks the active connection mode (chat / voice) so handleConnect can skip
    // mic permission in chat mode.
    const currentConnectionModeRef = useRef<ConnectionMode>('voice')
    // Signals that once the SDK reports an open connection, we should inject
    // an internal user cue that prompts the agent to greet first.
    const shouldSendArrivalGreetingOnConnectRef = useRef(false)
    // v1 startSession() is fire-and-forget. Defer parent "connected" signals
    // until the SDK's onConnect callback confirms the transport is ready.
    const shouldNotifySdkConnectRef = useRef(false)
    const didNotifyFailedConnectRef = useRef(false)
    const suppressNextGreetingRef = useRef(false)
    const pendingContextUpdateRef = useRef<string | null>(null)
    const messagesRef = useRef<Message[]>([])
    const arrivalGreetingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const conversationIdRef = useRef<string | null>(null)
    const disconnectIntentRef = useRef<DisconnectIntent>('unknown')
    const shouldResetConversationOnDisconnectRef = useRef(false)
    // The SDK may redeliver an event after reconnecting its transport. Keep one
    // transcript entry per server event for the lifetime of each conversation.
    const seenMessageEventIdsRef = useRef<Set<string>>(new Set())
    // A meeting-end instruction can produce repeated acknowledgements while
    // queued voice turns settle. Preserve the first acknowledgement only.
    const meetingEndAckRef = useRef<{ sentAt: number; displayed: boolean } | null>(null)
    const meetingToggleLockedRef = useRef(false)
    const meetingToggleUnlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Per-message thumbs up/down feedback state
    const [messageFeedback, setMessageFeedback] = useState<Record<string, MessageFeedback>>({})

    // Auto-resize textarea
    useEffect(() => {
        const textarea = textareaRef.current
        if (textarea) {
            textarea.style.height = 'auto'
            textarea.style.height = Math.min(textarea.scrollHeight, window.innerHeight * 0.5) + 'px'
        }
    }, [inputText])

    // Check for first-time user
    useEffect(() => {
        const hasSeenOnboarding = localStorage.getItem('botnexus_voice_onboarding_seen')
        if (!hasSeenOnboarding) {
            // Small delay to ensure layout is ready and to draw attention
            const timer = setTimeout(() => {
                setShowMeetingTip(true)
                setShowTranslateTip(true)
            }, 1000)
            return () => clearTimeout(timer)
        }
    }, [])

    const dismissMeetingTip = () => {
        setShowMeetingTip(false)
        // Save to localStorage when both tips are dismissed
        if (!showTranslateTip) {
            localStorage.setItem('botnexus_voice_onboarding_seen', 'true')
        }
    }

    const dismissTranslateTip = () => {
        setShowTranslateTip(false)
        // Save to localStorage when both tips are dismissed
        if (!showMeetingTip) {
            localStorage.setItem('botnexus_voice_onboarding_seen', 'true')
        }
    }

    // Enable PWA features for this agent
    useAgentPWA({ agentName })

    const conversation = useConversation({
        // Keys are tool names registered in the ElevenLabs agent; the agent can call
        // these functions directly from within a conversation turn.
        clientTools: clientTools ?? {},
        onConnect: ({ conversationId }) => {
            const requestedAgentMode = pendingAgentModeOnConnectRef.current
            pendingAgentModeOnConnectRef.current = null
            if (requestedAgentMode && agentModeRef.current !== requestedAgentMode) {
                setAgentMode(requestedAgentMode)
                agentModeRef.current = requestedAgentMode
                onAgentModeChange?.(requestedAgentMode)
            }
            setConnectionError(null)
            conversationIdRef.current = conversationId
            seenMessageEventIdsRef.current.clear()
            meetingEndAckRef.current = null
            isConnectingRef.current = false
            const shouldNotifySdkConnect = shouldNotifySdkConnectRef.current
            shouldNotifySdkConnectRef.current = false
            if (shouldNotifySdkConnect) {
                onSdkConnect?.()
            }
            onConnectionModeChange?.(currentConnectionModeRef.current)
            // Text conversations do not expose a microphone. Apply the persisted
            // mute state only after a voice transport has connected.
            if (currentConnectionModeRef.current === 'voice') {
                conversation.setMuted(isMicMuted)
            }

            if (import.meta.env.DEV) {
                console.log('[ElevenLabs Dev] Connected')

                // Log which transport is actually in use.
                // WebRTC (LiveKit) exposes an internal `_room` or `connection` object;
                // WebSocket fallback uses a plain `_ws` / `socket` field instead.
                const conv = conversation as any
                const transportType =
                    conv._room ? 'webrtc (LiveKit)' :
                    conv._ws || conv.socket ? 'websocket (fallback)' :
                    conv.connection?.type ?? 'unknown'
                console.log('[ElevenLabs Dev] Transport:', transportType)
                console.log('[ElevenLabs Dev] Conversation keys:', Object.keys(conv))

                // Verify audio output is working
                const outputVolume = conversation.getOutputVolume?.()
                const inputVolume = conversation.getInputVolume?.()
                console.log('[ElevenLabs Dev] Audio levels - Input:', inputVolume, 'Output:', outputVolume)

                // Check frequency data availability
                const freqData = conversation.getOutputByteFrequencyData?.()
                console.log('[ElevenLabs Dev] Has frequency data:', !!freqData)
            }
            if (shouldSendArrivalGreetingOnConnectRef.current && messagesRef.current.length === 0) {
                shouldSendArrivalGreetingOnConnectRef.current = false
                if (arrivalGreetingTimerRef.current) {
                    clearTimeout(arrivalGreetingTimerRef.current)
                    arrivalGreetingTimerRef.current = null
                }
                // Delay slightly so the transport is fully ready before we push
                // the internal bootstrap user message.
                arrivalGreetingTimerRef.current = setTimeout(() => {
                    arrivalGreetingTimerRef.current = null
                    if (statusRef.current !== 'connected') return

                    conversation.sendUserMessage(ARRIVAL_GREETING_CONTEXT_MESSAGE)
                    logger.info('[VoiceAgentSDK] Arrival greeting bootstrap user message sent after connect')
                }, ARRIVAL_GREETING_SEND_DELAY_MS)
            }
            if (pendingContextUpdateRef.current) {
                const contextToInject = pendingContextUpdateRef.current
                pendingContextUpdateRef.current = null
                setTimeout(() => {
                    if (statusRef.current !== 'connected') return
                    if (import.meta.env.DEV) {
                        console.log('[VoiceAgentSDK] History context injected after reconnect:', {
                            payload: contextToInject,
                            lines: contextToInject.split('\n')
                        })
                    }
                    conversation.sendContextualUpdate(contextToInject)
                    logger.info('[VoiceAgentSDK] History context injected after reconnect')
                }, ARRIVAL_GREETING_SEND_DELAY_MS + 200)
            }
            logger.info('[VoiceAgentSDK] Connected', {
                conversationId,
                agentMode: agentModeRef.current,
                connectionMode: currentConnectionModeRef.current,
                transport: transportRef.current,
                relay: relayRef.current,
            })
        },
        onDisconnect: (details) => {
            isConnectingRef.current = false
            shouldNotifySdkConnectRef.current = false
            const shouldNotifySdkDisconnect = !didNotifyFailedConnectRef.current
            didNotifyFailedConnectRef.current = false
            if (import.meta.env.DEV) {
                console.log('[ElevenLabs Dev] Disconnected')
            }
            const disconnectIntent = disconnectIntentRef.current
            const conversationId = conversationIdRef.current
            const shouldPreserveAgentMode =
                disconnectIntent === 'mode-switch' || pendingAgentModeOnConnectRef.current !== null
            disconnectIntentRef.current = 'unknown'
            conversationIdRef.current = null
            seenMessageEventIdsRef.current.clear()
            meetingEndAckRef.current = null
            onConnectionModeChange?.(null)
            const diagnosticDetails = {
                reason: details.reason,
                ...(details.reason !== 'user' ? {
                    closeCode: details.closeCode ?? details.context?.code,
                    message: safeVoiceError(details.reason === 'error' ? details.message : details.closeReason ?? ''),
                } : {}),
            }
            if (disconnectIntent === 'unknown' && details.reason === 'error') {
                setConnectionError(`Conversation disconnected: ${diagnosticDetails.message || 'Connection lost. Please try again.'}`)
                logger.error('[VoiceAgentSDK] Unexpected SDK disconnect', {
                    details: diagnosticDetails,
                    conversationId,
                    agentMode: agentModeRef.current,
                    connectionMode: currentConnectionModeRef.current,
                })
            } else {
                logger.info('[VoiceAgentSDK] Disconnected', { disconnectIntent, details: diagnosticDetails, conversationId })
            }
            // Terminal disconnects leave special modes. During an intentional
            // replacement-session transition, preserve the requested mode so
            // the old session cannot reset the new session to the default prompt.
            if (
                !shouldPreserveAgentMode &&
                (agentModeRef.current === 'meeting' || agentModeRef.current === 'translator')
            ) {
                setAgentMode('default')
                agentModeRef.current = 'default'
                onAgentModeChange?.('default')
            }
            // Reset conversation mode so status label returns to 'Listening' on next connect
            setConversationMode('listening')
            shouldSendArrivalGreetingOnConnectRef.current = false
            if (arrivalGreetingTimerRef.current) {
                clearTimeout(arrivalGreetingTimerRef.current)
                arrivalGreetingTimerRef.current = null
            }
            if (shouldResetConversationOnDisconnectRef.current) {
                shouldResetConversationOnDisconnectRef.current = false
                pendingContextUpdateRef.current = null
                messagesRef.current = []
                setMessages([])
                setInputText('')
                setMessageFeedback({})
            }
            if (shouldNotifySdkDisconnect) {
                onSdkDisconnect?.(disconnectIntent)
            }
        },
        onMessage: (message: any) => {
            const eventId = typeof message.event_id === 'number' || typeof message.event_id === 'string'
                ? String(message.event_id)
                : null
            const eventKey = eventId ? `${message.role ?? message.type ?? 'unknown'}:${eventId}` : null
            if (eventKey) {
                if (seenMessageEventIdsRef.current.has(eventKey)) {
                    logger.warn('[VoiceAgentSDK] Suppressed duplicate SDK message event', {
                        eventId,
                        role: message.role,
                    })
                    return
                }
                seenMessageEventIdsRef.current.add(eventKey)
            }

            if (import.meta.env.DEV) {
                console.log('[ElevenLabs Dev] Message received:', message)

                // Check audio levels when agent speaks
                if (message.role === 'agent') {
                    const outputVolume = conversation.getOutputVolume?.()
                    console.log('[ElevenLabs Dev] Agent speaking, output volume:', outputVolume)
                }
            }

            // In text-only (chat) mode, agent responses arrive as:
            //   { type: 'agent_response', agent_response: string }
            // rather than the normal { role: 'agent', message: string } shape.
            const msg = message as any
            if (msg.type === 'agent_response' && typeof msg.agent_response === 'string') {
                const text = msg.agent_response
                if (text) {
                    const meetingEndAck = meetingEndAckRef.current
                    if (
                        meetingEndAck &&
                        Date.now() - meetingEndAck.sentAt <= MEETING_END_ACK_DEDUPE_MS &&
                        isMeetingEndedAcknowledgement(text)
                    ) {
                        if (meetingEndAck.displayed) {
                            logger.warn('[VoiceAgentSDK] Suppressed duplicate meeting-end acknowledgement', {
                                eventId,
                                text,
                            })
                            return
                        }
                        meetingEndAck.displayed = true
                    }
                    setMessages(prev => [...prev, {
                        id: Math.random().toString(36).substring(7),
                        role: 'agent',
                        text,
                        timestamp: Date.now()
                    }])
                    onSdkMessage?.({ role: 'agent', text, timestamp: Date.now() })
                }
                return
            }

            // Hide the internal bootstrap user cue from the transcript UI.
            const isInternalArrivalBootstrapMessage =
                message.role === 'user' && message.message === ARRIVAL_GREETING_CONTEXT_MESSAGE
            if (isInternalArrivalBootstrapMessage) {
                if (import.meta.env.DEV) {
                    console.log('[ElevenLabs Dev] Skipping internal arrival bootstrap message in transcript')
                }
                return
            }
            // Filter out empty messages or technical updates if needed
            if (message.role === 'user' || message.role === 'agent') {
                const text = typeof message.message === 'string' ? message.message : ''
                if (!text) return

                const meetingEndAck = meetingEndAckRef.current
                if (
                    message.role === 'agent' &&
                    meetingEndAck &&
                    Date.now() - meetingEndAck.sentAt <= MEETING_END_ACK_DEDUPE_MS &&
                    isMeetingEndedAcknowledgement(text)
                ) {
                    if (meetingEndAck.displayed) {
                        logger.warn('[VoiceAgentSDK] Suppressed duplicate meeting-end acknowledgement', {
                            eventId,
                            text,
                        })
                        return
                    }
                    meetingEndAck.displayed = true
                }

                setMessages(prev => [...prev, {
                    id: Math.random().toString(36).substring(7),
                    role: message.role,
                    text,
                    timestamp: Date.now()
                }])
                onSdkMessage?.({ role: message.role, text, timestamp: Date.now() })
            }
        },
        onError: (error, context) => {
            const failedWhileConnecting = isConnectingRef.current
            const failedAgentMode = pendingAgentModeOnConnectRef.current
            pendingAgentModeOnConnectRef.current = null
            isConnectingRef.current = false
            shouldNotifySdkConnectRef.current = false
            if (failedWhileConnecting) {
                didNotifyFailedConnectRef.current = true
                onConnectionModeChange?.(null)
                onSdkDisconnect?.('unknown')
                if (failedAgentMode && failedAgentMode !== 'default') {
                    setAgentMode('default')
                    agentModeRef.current = 'default'
                    onAgentModeChange?.('default')
                }
            }
            if (import.meta.env.DEV) {
                logger.info('[VoiceAgentSDK] SDK error during connection:', failedWhileConnecting)
            }
            const diagnosticMessage = safeVoiceError(context instanceof Error ? context.message : error)
            setConnectionError(diagnosticMessage)
            logger.error('[VoiceAgentSDK] Error', {
                message: diagnosticMessage,
                agentId,
                conversationId: conversationIdRef.current,
                connectionMode: currentConnectionModeRef.current,
            })

            // More specific error handling
            const errorMessage = context instanceof Error ? context.message : error
            if (errorMessage.includes('microphone') || errorMessage.includes('audio')) {
                alert('Microphone error. Please check permissions and try again.')
            } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
                alert('Network error. Please check your connection.')
            }
        },
        onStatusChange: ({ status }) => logger.info('[VoiceAgentSDK] Status changed', { status, agentId }),
        onGuardrailTriggered: () => {
            logger.warn('[VoiceAgentSDK] ElevenLabs guardrail triggered', {
                conversationId: conversationIdRef.current,
                agentMode: agentModeRef.current,
            })
        },
        // Tracks the agent's current conversation mode (listening / speaking / thinking).
        // Used to show a more precise status label than the binary isSpeaking flag.
        onModeChange: ({ mode }) => {
            const modeValue = mode
            if (import.meta.env.DEV) {
                console.log('[ElevenLabs Dev] Mode changed:', modeValue)
            }
            setConversationMode(modeValue)
            onSdkModeChange?.(modeValue)
        }
    })

    const {
        status,
        isSpeaking,
        startSession,
        endSession,
        sendUserMessage,
        sendUserActivity,
    } = conversation
    const statusRef = useRef(status)
    const controlConnectRef = useRef<(mode: ConnectionMode) => Promise<void>>(async () => {})
    const controlDisconnectRef = useRef<() => Promise<void>>(async () => {})
    const controlSwitchModeRef = useRef<(mode: ConnectionMode) => Promise<void>>(async () => {})
    const controlToggleMicRef = useRef<() => void>(() => {})
    const controlToggleMeetingRef = useRef<() => Promise<void>>(async () => {})
    const controlToggleTranslatorRef = useRef<() => Promise<void>>(async () => {})
    const controlGetInputVolumeRef = useRef<() => number>(() => 0)
    const controlGetOutputFrequencyDataRef = useRef<() => Uint8Array | null>(() => null)
    const autoConnectAttemptedRef = useRef(false)

    useEffect(() => {
        statusRef.current = status
    }, [status])

    // Translator sessions are intentionally long-lived and may contain natural
    // pauses between speakers. User-activity events reset the turn timeout
    // without adding conversation content or triggering an agent response.
    useEffect(() => {
        if (status !== 'connected' || agentMode !== 'translator') return

        const interval = window.setInterval(() => {
            if (statusRef.current === 'connected' && agentModeRef.current === 'translator') {
                sendUserActivity()
            }
        }, TRANSLATOR_KEEP_ALIVE_MS)

        return () => window.clearInterval(interval)
    }, [agentMode, sendUserActivity, status])

    // Keep screen awake during active voice calls (prevents mobile screen from dimming)
    const isConnected = status === 'connected'
    const { requestWakeLockManually } = useWakeLock(isConnected)

    // Auto-connect only for non-shell layouts.
    // In shell mode, Connect should be user-driven from the control bar.
    useEffect(() => {
        if (!initialAutoConnect || autoConnectAttemptedRef.current) return
        if (status !== 'disconnected') return
        autoConnectAttemptedRef.current = true
        void controlConnectRef.current('voice')
    }, [initialAutoConnect, status])

    // Cleanup on unmount: end session if active
    // Using a ref to track the latest endSession function to avoid stale closure issues
    const endSessionRef = useRef(endSession)
    endSessionRef.current = endSession

    useEffect(() => {
        return () => {
            if (arrivalGreetingTimerRef.current) {
                clearTimeout(arrivalGreetingTimerRef.current)
                arrivalGreetingTimerRef.current = null
            }
            if (meetingToggleUnlockTimerRef.current) {
                clearTimeout(meetingToggleUnlockTimerRef.current)
                meetingToggleUnlockTimerRef.current = null
            }
            // Always try to end session on unmount - the SDK will handle if not connected.
            // ElevenLabs v1 ends synchronously, so cleanup errors are synchronous too.
            try {
                endSessionRef.current()
            } catch (error) {
                logger.error('[VoiceAgentSDK] Error ending session on unmount', error)
            }
        }
    }, [])

    // Auto-scroll to bottom of transcript
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [messages])

    useEffect(() => {
        messagesRef.current = messages
    }, [messages])

    // Opt-in audio diagnostics also work on production phones without DevTools.
    useEffect(() => {
        if (!isConnected || currentConnectionModeRef.current !== 'voice' || !showVoiceDiagnostics) return

        const interval = setInterval(() => {
            try {
                setAudioLevels({
                    input: conversation.getInputVolume(),
                    output: conversation.getOutputVolume(),
                })
            } catch (e) {
                // Diagnostics must not interfere with the call.
            }
        }, 1000)

        return () => clearInterval(interval)
    }, [isConnected, showVoiceDiagnostics, conversation.getInputVolume, conversation.getOutputVolume])

    // Expose imperative conversation actions to the parent component when connected.
    // The parent can use these to inject context (sendContextualUpdate) or signal
    // user activity (sendUserActivity) without needing to reach inside this component.
    useEffect(() => {
        if (isConnected && onActionsReady) {
            onActionsReady({
                sendContextualUpdate: (text: string) => {
                    conversation.sendContextualUpdate(text)
                    logger.info('[VoiceAgentSDK] Contextual update sent:', text)
                },
                sendUserActivity: () => {
                    conversation.sendUserActivity()
                }
            })
        }
    }, [isConnected]) // eslint-disable-line react-hooks/exhaustive-deps

    // Fire onControlsReady once on mount with stable wrappers that always call
    // the latest handler implementations.
    useEffect(() => {
        onControlsReady?.({
            connect: (mode: ConnectionMode) => {
                void controlConnectRef.current(mode)
            },
            disconnect: () => {
                void controlDisconnectRef.current()
            },
            switchMode: (mode: ConnectionMode) => {
                void controlSwitchModeRef.current(mode)
            },
            toggleMic: () => {
                controlToggleMicRef.current()
            },
            toggleMeeting: () => {
                void controlToggleMeetingRef.current()
            },
            toggleTranslator: () => {
                void controlToggleTranslatorRef.current()
            },
            getInputVolume: () => controlGetInputVolumeRef.current(),
            getOutputFrequencyData: () => controlGetOutputFrequencyDataRef.current(),
        })
    }, [onControlsReady])

    // Send thumbs-up/down feedback for a specific agent message.
    // Calls the SDK's sendFeedback and records the choice locally for UI feedback.
    const handleFeedback = (msgId: string, like: boolean) => {
        conversation.sendFeedback(like)
        setMessageFeedback(prev => ({ ...prev, [msgId]: like ? 'like' : 'dislike' }))
        logger.info('[VoiceAgentSDK] Feedback sent:', like ? 'like' : 'dislike')
    }

    // Track the last successfully fetched SAT token across reconnects
    const lastSATRef = useRef<string>('')

    // Ref to track connection state and prevent concurrent attempts
    const isConnectingRef = useRef(false)
    const waitForStatus = async (desiredStatuses: string[], timeoutMs = 3000) => {
        const start = performance.now()
        while (!desiredStatuses.includes(statusRef.current) && (performance.now() - start) < timeoutMs) {
            await new Promise(resolve => setTimeout(resolve, 50))
        }
    }

    const handleConnect = async (
        mode: ConnectionMode = 'voice',
        requestedAgentMode: AgentMode = agentModeRef.current,
    ) => {
        const isModeSwitch = suppressNextGreetingRef.current
        let sessionStartRequested = false
        if (isConnectingRef.current) {
            if (import.meta.env.DEV) {
                console.log('[VoiceAgentSDK] Connection attempt ignored - already connecting')
            }
            return
        }

        try {
            setConnectionError(null)
            setAudioLevels({ input: 0, output: 0 })
            isConnectingRef.current = true
            pendingAgentModeOnConnectRef.current = requestedAgentMode
            didNotifyFailedConnectRef.current = false
            currentConnectionModeRef.current = mode
            onConnecting?.()

            // Request wake lock immediately during user gesture (required for iOS)
            // This must happen synchronously in the click handler before any awaits
            requestWakeLockManually()

            // Safeguard: Ensure any previous session is fully cleaned up
            if (statusRef.current !== 'disconnected') {
                try {
                    await endSession()
                } catch (e) {
                    // Ignore errors here, just want to ensure clean slate
                }
                await waitForStatus(['disconnected'], 2000)
            }

            // Small delay to allow cleanup to complete
            await new Promise(resolve => setTimeout(resolve, 200))

            // The SDK owns microphone permission, stream lifetime, and Android's
            // audio initialization delay. Do not open/stop a second stream here.

            // Fetch a fresh SAT on every connect attempt
            let sat = lastSATRef.current
            if (onRefreshSAT) {
                try {
                    const freshToken = await onRefreshSAT()
                    if (freshToken) {
                        sat = freshToken
                        lastSATRef.current = freshToken
                    } else {
                        logger.warn('[VoiceAgentSDK] SAT refresh returned null, using previous token')
                    }
                } catch (error) {
                    logger.warn('[VoiceAgentSDK] SAT refresh failed, using previous token', error)
                }
            }

            // Format current date, time, and timezone for agent scheduling tasks
            const now = new Date();

            const clientTime = `${now.toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short',
            })} | ${Intl.DateTimeFormat().resolvedOptions().timeZone}`;

            // Fetch signed URL (websocket/chat) or conversation token (webrtc/voice).
            // Stop on authentication failure; private agents cannot use an unsigned fallback.
            const { connectionType, relay } = getVoiceTransport(mode, window.location.search)
            transportRef.current = connectionType
            relayRef.current = relay
            logger.info('[VoiceAgentSDK] Requesting authentication', { agentId, connectionType, relay })
            const { data: { session }, error: sessionError } = await supabase.auth.getSession()
            if (sessionError || !session?.access_token) {
                throw new Error('Your sign-in session is unavailable. Please sign in again.')
            }
            const authToken = await requestElevenLabsAuth(config.apiURL, session.access_token, agentId, connectionType)
            logger.info('[VoiceAgentSDK] Authentication received', { agentId, connectionType })

            const dynamicVariables = {
                user_id: userId,
                auth_user_id: authUserId || '',
                bot_name: agentName,
                bot_id: botId,
                agent_id: agentId,
                role: role || 'user',
                full_name: fullName || 'User',
                email: email || '',
                provider: provider || '',
                issuer: issuer || '',
                provider_id: providerId || '',
                phone: phone || '',
                client_time: clientTime,
                sat
            }
            const sessionOptions: SessionConfig = 'conversationToken' in authToken
                ? {
                    ...authToken,
                    connectionType: 'webrtc',
                    ...(relay ? { webRtc: { iceTransportPolicy: 'relay' as const } } : {}),
                    dynamicVariables,
                }
                : {
                    ...authToken,
                    connectionType: 'websocket',
                    dynamicVariables,
                }

            // For default sessions we suppress the built-in first message and
            // then inject a contextual cue on connect so the greeting is
            // triggered intentionally.
            // This value was captured synchronously when the transition began.
            // Do not read agentModeRef here: the outgoing session may have
            // completed its delayed onDisconnect callback during the awaits above.
            const modeAtConnect = requestedAgentMode
            const isTranslatorMode = modeAtConnect === 'translator'
            const shouldBootstrapArrivalGreeting = modeAtConnect === 'default'
            shouldSendArrivalGreetingOnConnectRef.current = shouldBootstrapArrivalGreeting
            // Mode switch suppresses greeting
            if (isModeSwitch) {
                shouldSendArrivalGreetingOnConnectRef.current = false
                suppressNextGreetingRef.current = false
            }
            if (shouldBootstrapArrivalGreeting) {
                sessionOptions.overrides = {
                    ...(sessionOptions.overrides ?? {}),
                    agent: {
                        ...(sessionOptions.overrides?.agent ?? {}),
                        firstMessage: ''
                    }
                }
            }

            // Apply translator overrides from the mode captured at the start
            // of this replacement-session transaction.
            if (isTranslatorMode) {
                shouldSendArrivalGreetingOnConnectRef.current = false
                sessionOptions.overrides = {
                    agent: {
                        prompt: {
                            prompt: TRANSLATOR_SYSTEM_PROMPT
                        },
                        firstMessage: "Translator mode engaged. Please specify the languages."
                    }
                }
            }

            if (mode === 'chat') {
                sessionOptions.overrides = {
                    ...(sessionOptions.overrides ?? {}),
                    conversation: { textOnly: true },
                }
            }

            logger.info('[VoiceAgentSDK] Starting session', { agentId, connectionType, relay, agentMode: modeAtConnect })

            shouldNotifySdkConnectRef.current = !isModeSwitch
            // The outgoing session's intent has been consumed. Once the new
            // start is issued, any subsequent disconnect belongs to this
            // session and must not inherit a stale "mode-switch" intent.
            disconnectIntentRef.current = 'unknown'
            startSession(sessionOptions)
            sessionStartRequested = true

            if (import.meta.env.DEV) {
                console.log('[VoiceAgentSDK] Session start requested', {
                    agentMode: modeAtConnect,
                    connectionMode: mode,
                    hasTranslatorPromptOverride: isTranslatorMode,
                })
            }
        } catch (error) {
            pendingAgentModeOnConnectRef.current = null
            shouldSendArrivalGreetingOnConnectRef.current = false
            if (arrivalGreetingTimerRef.current) {
                clearTimeout(arrivalGreetingTimerRef.current)
                arrivalGreetingTimerRef.current = null
            }
            const diagnosticMessage = safeVoiceError(error instanceof Error ? error.message : 'Could not start conversation.')
            setConnectionError(diagnosticMessage)
            logger.error('[VoiceAgentSDK] Failed to connect', { message: diagnosticMessage, agentId, connectionMode: mode })
            if (requestedAgentMode !== 'default') {
                setAgentMode('default')
                agentModeRef.current = 'default'
                onAgentModeChange?.('default')
            }
            if (!didNotifyFailedConnectRef.current) {
                didNotifyFailedConnectRef.current = true
                onConnectionModeChange?.(null)
                onSdkDisconnect?.('unknown')
            }

            // More specific error messages
            if (error instanceof DOMException) {
                if (error.name === 'NotAllowedError') {
                    alert('Microphone permission denied. Please allow microphone access and try again.')
                } else if (error.name === 'NotFoundError') {
                    alert('No microphone found. Please connect a microphone and try again.')
                } else {
                    alert(`Microphone error: ${error.message}`)
                }
            } else {
                alert(diagnosticMessage)
            }
        } finally {
            if (!sessionStartRequested) {
                isConnectingRef.current = false
                shouldNotifySdkConnectRef.current = false
            }
        }
    }

    const performDisconnect = async ({
        intent = 'unknown',
        resetConversation = false,
    }: {
        intent?: DisconnectIntent
        resetConversation?: boolean
    } = {}) => {
        disconnectIntentRef.current = intent
        shouldResetConversationOnDisconnectRef.current = resetConversation

        if (resetConversation) {
            pendingContextUpdateRef.current = null
            suppressNextGreetingRef.current = false
        }

        try {
            if (import.meta.env.DEV) {
                console.log('[VoiceAgentSDK] Disconnecting...')
            }
            await endSession()
            if (import.meta.env.DEV) {
                console.log('[VoiceAgentSDK] Session ended successfully')
            }
        } catch (error) {
            logger.error('[VoiceAgentSDK] Error ending session', error)
            // SDK handles all audio stream cleanup internally
        }
    }

    const handleDisconnect = async () => {
        await performDisconnect({ intent: 'manual', resetConversation: true })
    }

    const ensureDisconnected = async (intent: DisconnectIntent = 'unknown') => {
        if (statusRef.current === 'disconnected') return
        await performDisconnect({ intent })
        await waitForStatus(['disconnected'], 4000)
    }

    const switchMode = async (newMode: ConnectionMode) => {
        const agentModeAtSwitch = agentModeRef.current
        if (messagesRef.current.length > 0) {
            const lines = messagesRef.current
                .map(m => `${m.role === 'user' ? 'User' : 'Agent'}: ${m.text}`)
                .join('\n')
            pendingContextUpdateRef.current =
                'Earlier in this session we discussed the following:\n\n' + lines +
                '\n\nPlease continue the conversation with this context in mind. Do not re-introduce yourself.'
        } else {
            pendingContextUpdateRef.current = null
        }
        suppressNextGreetingRef.current = true
        await ensureDisconnected('mode-switch')
        await new Promise(resolve => setTimeout(resolve, 300))
        await handleConnect(newMode, agentModeAtSwitch)
    }

    const handleSendMessage = () => {
        if (!inputText.trim()) return
        if (!isConnected) return
        sendUserMessage(inputText)
        // Optimistically add user message
        setMessages(prev => [...prev, {
            id: Math.random().toString(36).substring(7),
            role: 'user',
            text: inputText,
            timestamp: Date.now()
        }])
        setInputText('')
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSendMessage()
        }
    }

    const handleCopyMessage = (text: string) => {
        navigator.clipboard.writeText(text)
    }

    const toggleMic = () => {
        if (currentConnectionModeRef.current !== 'voice' || statusRef.current !== 'connected') return

        const next = !isMicMuted
        conversation.setMuted(next)
        setIsMicMuted(next)
        onMicMutedChange?.(next)
    }

    const toggleMeetingMode = async () => {
        if (statusRef.current !== 'connected') return
        if (meetingToggleLockedRef.current) {
            logger.warn('[VoiceAgentSDK] Ignored repeated meeting-mode toggle during transition')
            return
        }

        meetingToggleLockedRef.current = true
        if (meetingToggleUnlockTimerRef.current) {
            clearTimeout(meetingToggleUnlockTimerRef.current)
        }
        meetingToggleUnlockTimerRef.current = setTimeout(() => {
            meetingToggleLockedRef.current = false
            meetingToggleUnlockTimerRef.current = null
        }, MEETING_TOGGLE_LOCK_MS)

        if (agentModeRef.current === 'meeting') {
            // Switch back to normal
            setAgentMode('default')
            agentModeRef.current = 'default'
            onAgentModeChange?.('default')
            meetingEndAckRef.current = { sentAt: Date.now(), displayed: false }
            const endMessage = " --- the meeting has now ended. Acknowledge exactly once by saying: Meeting ended."
            if (import.meta.env.DEV) {
                console.log('[Meeting Mode] Sending end message:', endMessage)
            }
            sendUserMessage(endMessage)
        } else {
            // Activate meeting mode
            meetingEndAckRef.current = null
            setAgentMode('meeting')
            agentModeRef.current = 'meeting'
            onAgentModeChange?.('meeting')
            // Don't mute mic here because user wants to speak to dictate notes,
            // but we might want to ensure the agent knows to just listen.
            const startMessage = "From now on we are in a meeting mode, write notes silently based on the conversation as it goes, until meeting is over, do not say anything until meeting is over. meeting will end when I will tell you it has ended accompanied by:---. Please Acknowledge this message by saying meeting started."
            if (import.meta.env.DEV) {
                console.log('[Meeting Mode] Sending start message:', startMessage)
            }
            sendUserMessage(startMessage)
        }
    }

    const toggleTranslatorMode = async () => {
        if (agentModeRef.current === 'translator') {
            // Toggle OFF: Set mode to default and disconnect
            setAgentMode('default')
            agentModeRef.current = 'default'
            onAgentModeChange?.('default')
            pendingAgentModeOnConnectRef.current = null
            await ensureDisconnected('manual')
        } else {
            // Toggle ON: Set translator mode and auto-connect
            // If connected/connecting, we need to disconnect first (can't change system prompt mid-session)
            await ensureDisconnected('mode-switch')

            setAgentMode('translator')
            agentModeRef.current = 'translator'
            onAgentModeChange?.('translator')

            // Auto-start the call with translator mode
            if (import.meta.env.DEV) {
                console.log('[Translator Mode] Auto-starting call with translator prompt')
            }

            await handleConnect('voice', 'translator')
        }
    }

    // Keep shell-control handlers current without rebinding the control bridge.
    controlConnectRef.current = handleConnect
    controlDisconnectRef.current = handleDisconnect
    controlSwitchModeRef.current = switchMode
    controlToggleMicRef.current = toggleMic
    controlToggleMeetingRef.current = toggleMeetingMode
    controlToggleTranslatorRef.current = toggleTranslatorMode
    controlGetInputVolumeRef.current = () => conversation.getInputVolume?.() ?? 0
    controlGetOutputFrequencyDataRef.current = () => conversation.getOutputByteFrequencyData?.() ?? null

    return (
        <div className={cn(
            "flex w-full flex-col overflow-hidden border-slate-800 bg-slate-950 shadow-2xl",
            // Desktop: fixed height, rounded, bordered
            // Mobile (or forced mobile): full height, no radius, no border
            forceMobileMode
                ? "h-full rounded-none border-0"
                : "md:h-[600px] md:rounded-2xl md:border h-full rounded-none border-0"
        )}>
            {/* Main Content Area — always flex-col so the visualizer stays as a top strip */}
            <div className="relative flex flex-1 flex-col overflow-hidden">

                {/* Visualizer / Spectrum Section — full-width strip at top on all breakpoints */}
                <div className="flex flex-col items-center justify-center bg-gradient-to-b from-slate-950 to-slate-900 relative overflow-hidden h-[90px] shrink-0 border-b border-slate-800">
                    <div className="relative flex h-full w-full items-center justify-center">
                        <VoiceAgentFace
                            isConnected={isConnected}
                            isSpeaking={isSpeaking}
                            status={status}
                            getFrequencyData={() => conversation.getOutputByteFrequencyData?.() ?? null}
                        />
                    </div>
                </div>

                {connectionError && (
                    <div role="alert" className="shrink-0 border-b border-red-900/50 bg-red-950/40 px-4 py-3 text-sm text-red-200">
                        {connectionError}
                    </div>
                )}

                {showVoiceDiagnostics && (
                    <div className="shrink-0 border-b border-slate-800 px-4 py-2 text-xs text-slate-400" aria-label="Voice diagnostics">
                        <div>Connection: {status} · {transportRef.current}{relayRef.current ? ' (relay)' : ''}</div>
                        <div>Mic input (local): {isConnected ? audioLevels.input.toFixed(3) : '—'} · Agent audio (SDK): {isConnected ? audioLevels.output.toFixed(3) : '—'} · {isMicMuted ? 'Mic muted' : 'Mic unmuted'}</div>
                        <div className="break-all select-text">Conversation: {conversationIdRef.current ?? 'Not connected'}</div>
                    </div>
                )}

                {/* Tools section — inline below visualizer */}
                {topSlot != null && (
                    <div className="shrink-0 border-b border-slate-800 overflow-y-auto">
                        {topSlot}
                    </div>
                )}

                {/* Transcript / Chat Section */}
                <div className={cn(
                    "flex w-full flex-col bg-slate-950/50 flex-1 min-h-0"
                )}>
                    <div className="flex-1 min-h-0 overflow-y-auto p-4 scroll-smooth">
                        <div className="flex flex-col gap-4">
                            {messages.length === 0 && (
                                <div className="mt-10 text-center text-sm text-slate-500">
                                    No messages yet. Start the conversation!
                                </div>
                            )}
                            {messages.map((msg) => (
                                // 'group' on the outer div so both copy and feedback buttons
                                // respond to hover over the entire message area
                                <div
                                    key={msg.id}
                                    className={cn(
                                        "group flex w-full flex-col gap-1",
                                        msg.role === 'user' ? "items-end" : "items-start"
                                    )}
                                >
                                    <div
                                        className="relative"
                                        onContextMenu={(e) => {
                                            e.preventDefault()
                                            handleCopyMessage(msg.text)
                                        }}
                                    >
                                        <div className={cn(
                                            "max-w-[85%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap",
                                            msg.role === 'user'
                                                ? "bg-emerald-600 text-white rounded-br-none"
                                                : "bg-slate-800 text-slate-200 rounded-bl-none"
                                        )}>
                                            {msg.text}
                                        </div>
                                        <button
                                            onClick={() => handleCopyMessage(msg.text)}
                                            className={cn(
                                                "absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-full bg-slate-800/80 text-slate-400 hover:text-white hover:bg-slate-700",
                                                msg.role === 'user' ? "-left-5" : "-right-5"
                                            )}
                                            title="Copy to clipboard"
                                        >
                                            <Copy className="h-3 w-3" />
                                        </button>
                                    </div>

                                    {/* Thumbs up / down feedback — agent messages only.
                                        Fades in on hover; buttons stay filled once voted. */}
                                    {msg.role === 'agent' && (
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => handleFeedback(msg.id, true)}
                                                className={cn(
                                                    "p-1 rounded-full transition-colors",
                                                    messageFeedback[msg.id] === 'like'
                                                        ? "text-emerald-400"
                                                        : "text-slate-600 hover:text-emerald-400"
                                                )}
                                                title="Good response"
                                            >
                                                <ThumbsUp className="h-3 w-3" />
                                            </button>
                                            <button
                                                onClick={() => handleFeedback(msg.id, false)}
                                                className={cn(
                                                    "p-1 rounded-full transition-colors",
                                                    messageFeedback[msg.id] === 'dislike'
                                                        ? "text-red-400"
                                                        : "text-slate-600 hover:text-red-400"
                                                )}
                                                title="Bad response"
                                            >
                                                <ThumbsDown className="h-3 w-3" />
                                            </button>
                                        </div>
                                    )}

                                    <span className="text-[10px] text-slate-600">
                                        {msg.role === 'user' ? 'You' : agentName} • {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            ))}
                            <div ref={scrollRef} />
                        </div>
                    </div>

                    {/* Input Area */}
                    <div
                        className="shrink-0 border-t border-slate-800 bg-slate-950 p-3 sm:p-4"
                        style={forceMobileMode ? { paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' } : undefined}
                    >
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="shrink-0 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                                onClick={() => alert('File upload coming soon!')}
                                disabled={!isConnected}
                                title="Upload File"
                            >
                                <FileUp className="h-5 w-5" />
                            </Button>

                            <div className="relative flex-1 min-w-0">
                                <textarea
                                    ref={textareaRef}
                                    value={inputText}
                                    onChange={(e) => {
                                        setInputText(e.target.value)
                                        // Optional: keep this opt-in so typing does not
                                        // change default turn-taking behavior.
                                        if (isConnected && autoSignalUserActivityOnType) {
                                            conversation.sendUserActivity()
                                        }
                                    }}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Type a message..."
                                    rows={1}
                                    className="flex w-full resize-none rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-50 min-h-[40px] max-h-[50vh]"
                                />
                            </div>

                            <Button
                                size="icon"
                                variant="ghost"
                                className="shrink-0 h-10 w-10 text-slate-400 hover:bg-slate-800 hover:text-emerald-400 self-end mb-0.5" // Align to bottom when textarea grows
                                onClick={handleSendMessage}
                                disabled={!isConnected || !inputText.trim()}
                            >
                                <Send className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
